import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageSquare, Send, ChevronDown } from 'lucide-react';
import DraggableModal from './DraggableModal';

// Decodeer HTML entities in image data (XSS-middleware escapet / naar &#x2F;)
const decodeImageData = (data) => {
  if (!data) return null;
  return data
    .replace(/&#x2F;/g, '/')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
};

const EMOJIS = ['😊','😌','✅','📦','🚛','✏️','☎️','🖅','📷','💼','📄','🔶'];

const formatTijd = (dt) => {
  if (!dt) return '';
  const d = new Date(dt);
  const nu = new Date();
  const gisteren = new Date(nu); gisteren.setDate(nu.getDate() - 1);
  if (d.toDateString() === nu.toDateString())
    return d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === gisteren.toDateString())
    return 'Gisteren ' + d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  return d.toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit' }) + ' ' +
    d.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
};

// ─── Custom klanten dropdown met rode badge per klant ───────────────────────
const KlantenDropdown = ({ alleKlanten, ongelezen, selectedKlantId, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Sluit dropdown bij klik buiten
  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const geselecteerd = alleKlanten.find(k => String(k.customerId) === String(selectedKlantId));
  const ongelBijGeselecteerd = geselecteerd ? (ongelezen[String(geselecteerd.customerId)] || 0) : 0;

  // Sorteer: klanten met ongelezen berichten bovenaan
  const gesorteerd = [...alleKlanten].sort((a, b) => {
    const oa = ongelezen[String(a.customerId)] || 0;
    const ob = ongelezen[String(b.customerId)] || 0;
    return ob - oa;
  });

  return (
    <div ref={ref} style={{ position: 'relative', flex: 1 }}>
      {/* Trigger knop */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: '100%', padding: '6px 10px',
          border: `1px solid ${ongelBijGeselecteerd > 0 ? '#ef4444' : '#cbd5e1'}`,
          borderRadius: '6px', fontSize: '13px',
          background: ongelBijGeselecteerd > 0 ? '#fef2f2' : 'white',
          color: geselecteerd ? (ongelBijGeselecteerd > 0 ? '#dc2626' : '#374151') : '#9ca3af',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', textAlign: 'left',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {ongelBijGeselecteerd > 0 && (
            <span style={{
              background: '#ef4444', color: 'white', borderRadius: '50%',
              width: '18px', height: '18px', fontSize: '11px', fontWeight: '700',
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
            }}>{ongelBijGeselecteerd}</span>
          )}
          {geselecteerd ? geselecteerd.naam : '— Selecteer klant —'}
        </span>
        <ChevronDown size={14} style={{ opacity: 0.5, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s' }} />
      </button>

      {/* Dropdown lijst */}
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: 'white', border: '1px solid #e2e8f0', borderRadius: '8px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)', marginTop: '4px',
          maxHeight: '240px', overflowY: 'auto',
        }}>
          {/* Lege optie */}
          <div
            onClick={() => { onChange(''); setOpen(false); }}
            style={{
              padding: '8px 12px', cursor: 'pointer', fontSize: '13px',
              color: '#9ca3af', borderBottom: '1px solid #f1f5f9',
              background: !selectedKlantId ? '#f0f7fa' : 'white',
            }}
            onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
            onMouseLeave={e => e.currentTarget.style.background = !selectedKlantId ? '#f0f7fa' : 'white'}
          >
            — Selecteer klant —
          </div>

          {/* Klanten gesorteerd — met ongelezen bovenaan in rood */}
          {gesorteerd.map(k => {
            const ong = ongelezen[String(k.customerId)] || 0;
            const isSelected = String(k.customerId) === String(selectedKlantId);
            return (
              <div
                key={k.customerId}
                onClick={() => { onChange(String(k.customerId)); setOpen(false); }}
                style={{
                  padding: '8px 12px', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  fontSize: '13px', fontWeight: ong > 0 ? '700' : '400',
                  color: ong > 0 ? '#dc2626' : '#374151',
                  background: isSelected ? '#f0f7fa' : (ong > 0 ? '#fff5f5' : 'white'),
                  borderBottom: '1px solid #f9fafb',
                }}
                onMouseEnter={e => e.currentTarget.style.background = ong > 0 ? '#fee2e2' : '#f8fafc'}
                onMouseLeave={e => e.currentTarget.style.background = isSelected ? '#f0f7fa' : (ong > 0 ? '#fff5f5' : 'white')}
              >
                <span>{k.naam}</span>
                {ong > 0 && (
                  <span style={{
                    background: '#ef4444', color: 'white', borderRadius: '10px',
                    padding: '1px 7px', fontSize: '11px', fontWeight: '700', flexShrink: 0
                  }}>{ong} ongelezen</span>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── Hoofd component ─────────────────────────────────────────────────────────
const BerichtenModal = ({ onClose }) => {
  const [alleKlanten, setAlleKlanten] = useState([]);
  const [selectedKlantId, setSelectedKlantId] = useState('');
  const [aanvragen, setAanvragen] = useState([]);
  const [selectedAanvraag, setSelectedAanvraag] = useState('');
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [totaalOngelezen, setTotaalOngelezen] = useState(0);
  const [ongelezen, setOngelezen] = useState({}); // { customerId: count }
  const [ongelezenaAanvragen, setOngelezenAanvragen] = useState(new Set()); // aanvraag_ids met ongelezen klant berichten
  const [nieuwBericht, setNieuwBericht] = useState('');
  const [sending, setSending] = useState(false);
  const [fotoPreview, setFotoPreview] = useState(null);
  const [fotoData, setFotoData] = useState(null);

  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  const chatRef = useRef(null);

  // Laad klanten + ongelezen per klant in één keer
  useEffect(() => {
    // all-customer-messages geeft: { klanten: [{customerId, naam, ongelezen}], totaalOngelezen }
    // portal-klanten geeft: { klanten: [{customerId, naam, ...}] } — ook klanten zonder berichten
    Promise.all([
      fetch('/api/admin/portal-klanten').then(r => r.json()).catch(() => ({ success: false })),
      fetch('/api/admin/all-customer-messages').then(r => r.json()).catch(() => ({ success: false })),
    ]).then(([portalData, berichtenData]) => {
      // Gebruik portal-klanten als basis (bevat ook klanten zonder berichten)
      if (portalData.success) setAlleKlanten(portalData.klanten || []);

      if (berichtenData.success) {
        setTotaalOngelezen(berichtenData.totaalOngelezen || 0);
        // Bouw map: customerId -> ongelezen count
        const map = {};
        (berichtenData.klanten || []).forEach(k => {
          const id = String(k.customerId || k.ID || k.id);
          map[id] = k.ongelezen || 0;
        });
        setOngelezen(map);
      }
    });
  }, []);

  // Laad aanvragen als klant verandert
  useEffect(() => {
    setSelectedAanvraag('');
    setAanvragen([]);
    setMessages([]);
    if (!selectedKlantId) return;
    fetch(`/api/admin/customer-messages/${selectedKlantId}/aanvragen`)
      .then(r => r.json())
      .then(d => { if (d.success) setAanvragen(d.aanvragen || []); })
      .catch(() => {});
  }, [selectedKlantId]);

  // Laad berichten
  const laadBerichten = useCallback(async (isInit = false) => {
    if (!selectedKlantId) { setMessages([]); return; }
    if (isInit) setLoadingMessages(true);
    try {
      const res = await fetch(`/api/admin/customer-messages/${selectedKlantId}`);
      const data = await res.json();
      if (data.success) {
        const msgs = selectedAanvraag
          ? (data.messages || []).filter(m => m.aanvraag_id === selectedAanvraag)
          : (data.messages || []);
        // Sorteer altijd op created_at ASC: oudste bovenaan, nieuwste onderaan
        const gesorteerd = [...msgs].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
        setMessages(prev => {
          const prevIds = prev.map(m => m.id).join(',');
          const newIds = gesorteerd.map(m => m.id).join(',');
          return prevIds === newIds ? prev : gesorteerd;
        });
        // Bereken welke aanvragen ongelezen klant berichten hebben
        const ongAanvraagIds = new Set(
          (data.messages || [])
            .filter(m => m.sender_type === 'customer' && !m.is_read && m.aanvraag_id)
            .map(m => m.aanvraag_id)
        );
        setOngelezenAanvragen(ongAanvraagIds);
        // Reset ongelezen teller voor deze klant na openen
        setOngelezen(prev => ({ ...prev, [String(selectedKlantId)]: 0 }));
      }
    } catch(e) {}
    if (isInit) setLoadingMessages(false);
  }, [selectedKlantId, selectedAanvraag]);

  useEffect(() => {
    laadBerichten(true);
    if (!selectedKlantId) return;
    const interval = setInterval(() => laadBerichten(false), 10000);
    return () => clearInterval(interval);
  }, [laadBerichten, selectedKlantId]);

  // Scroll naar beneden: instant bij eerste keer laden, smooth bij nieuw bericht
  const prevMsgCount = useRef(0);
  useEffect(() => {
    if (!chatRef.current) return;
    const isFirstLoad = prevMsgCount.current === 0 && messages.length > 0;
    if (isFirstLoad) {
      // Direct naar beneden zonder animatie bij eerste load
      chatRef.current.scrollTop = chatRef.current.scrollHeight;
    } else if (messages.length > prevMsgCount.current) {
      // Nieuw bericht — smooth scrollen
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
    prevMsgCount.current = messages.length;
  }, [messages]);

  const geselecteerdeKlant = alleKlanten.find(k => String(k.customerId) === String(selectedKlantId));

  const handleFotoUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => { setFotoPreview(ev.target.result); setFotoData(ev.target.result); };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const verstuur = async () => {
    if ((!nieuwBericht.trim() && !fotoData) || sending || !selectedKlantId) return;
    setSending(true);
    try {
      const berichtTekst = fotoData
        ? (nieuwBericht.trim() ? nieuwBericht.trim() + '\n[foto]' : '[foto]')
        : nieuwBericht.trim();
      const gesAanvraag = aanvragen.find(a => a.AanvraagID === selectedAanvraag);
      const res = await fetch('/api/admin/customer-message', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          customerId: selectedKlantId,
          message: berichtTekst,
          imageData: fotoData || null,
          aanvraagId: selectedAanvraag || null,
          onderwerp: gesAanvraag
            ? `${gesAanvraag.AanvraagID} — ${gesAanvraag.LaadPlaats} → ${gesAanvraag.LosPlaats}`
            : (selectedAanvraag || null)
        })
      });
      const data = await res.json();
      if (data.success) {
        setNieuwBericht('');
        setFotoPreview(null);
        setFotoData(null);
        await laadBerichten();
        inputRef.current?.focus();
      }
    } catch(e) {}
    setSending(false);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); verstuur(); }
  };

  return (
    <DraggableModal
      isOpen={true}
      title={`💼 Berichten${totaalOngelezen > 0 ? ` (${totaalOngelezen} ongelezen)` : ''}`}
      onClose={onClose}
      width={540}
      initialPosition={{ x: Math.max(50, Math.round((window.innerWidth - 540) / 2)), y: 80 }}
    >
      <div style={{ height: '580px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>

        {/* ── Twee dropdowns bovenaan ── */}
        <div style={{ padding: '12px 16px', borderBottom: '2px solid #e5e7eb', background: '#f0f7fa', display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {/* Klant — custom dropdown met rode badges */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#1a364c', width: '60px', flexShrink: 0 }}>Klant:</label>
            <KlantenDropdown
              alleKlanten={alleKlanten}
              ongelezen={ongelezen}
              selectedKlantId={selectedKlantId}
              onChange={setSelectedKlantId}
            />
          </div>

          {/* Aanvraag */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <label style={{ fontSize: '12px', fontWeight: '600', color: '#1a364c', width: '60px', flexShrink: 0 }}>Aanvraag:</label>
            <select
              value={selectedAanvraag}
              onChange={e => setSelectedAanvraag(e.target.value)}
              disabled={!selectedKlantId}
              style={{ flex: 1, padding: '6px 10px', border: '1px solid #cbd5e1', borderRadius: '6px', fontSize: '13px', background: selectedKlantId ? 'white' : '#f9fafb', color: selectedKlantId ? '#374151' : '#9ca3af' }}
            >
              <option value="">— Algemeen (alle berichten) —</option>
              {aanvragen.map(a => (
                <option key={a.AanvraagID} value={a.AanvraagID}
                  style={{
                    background: ongelezenaAanvragen.has(a.AanvraagID) ? '#fef2f2' : undefined,
                    color: ongelezenaAanvragen.has(a.AanvraagID) ? '#dc2626' : undefined,
                    fontWeight: ongelezenaAanvragen.has(a.AanvraagID) ? 700 : undefined
                  }}>
                  {ongelezenaAanvragen.has(a.AanvraagID) ? '🔴 ' : ''}{a.AanvraagID} — {a.LaadPlaats} → {a.LosPlaats}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* ── Berichten ── */}
        <div ref={chatRef} style={{ flex: 1, overflowY: 'auto', padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px', background: '#f9fafb' }}>
          {!selectedKlantId ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '60px' }}>
              <MessageSquare size={36} style={{ margin: '0 auto 10px', opacity: 0.2 }} />
              <p style={{ fontSize: '13px' }}>Selecteer eerst een klant</p>
              {Object.values(ongelezen).some(v => v > 0) && (
                <p style={{ fontSize: '12px', color: '#ef4444', marginTop: '8px', fontWeight: '600' }}>
                  🔴 Klanten met ongelezen berichten staan rood in de lijst
                </p>
              )}
            </div>
          ) : loadingMessages ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '40px' }}>Laden...</div>
          ) : messages.length === 0 ? (
            <div style={{ textAlign: 'center', color: '#9ca3af', paddingTop: '40px' }}>
              <MessageSquare size={32} style={{ margin: '0 auto 8px', opacity: 0.3 }} />
              <p style={{ fontSize: '13px' }}>Geen berichten{selectedAanvraag ? ' voor deze aanvraag' : ''}</p>
            </div>
          ) : messages.map(m => {
            const isAdmin = m.sender_type === 'admin';
            return (
              <div key={m.id} style={{ display: 'flex', justifyContent: isAdmin ? 'flex-end' : 'flex-start' }}>
                <div style={{
                  maxWidth: '78%', padding: '10px 14px',
                  borderRadius: isAdmin ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
                  background: isAdmin ? '#1a364c' : 'white',
                  color: isAdmin ? 'white' : '#1f2937',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)', fontSize: '13px', lineHeight: '1.5'
                }}>
                  {m.aanvraag_id && !selectedAanvraag && (
                    <div style={{ fontSize: '10px', opacity: 0.65, marginBottom: '4px' }}>
                      📦 {m.onderwerp || m.aanvraag_id}
                    </div>
                  )}
                  {m.image_data && (
                    <img src={decodeImageData(m.image_data)} alt="afbeelding"
                      style={{ maxWidth: '200px', borderRadius: '8px', display: 'block', marginBottom: '6px' }} />
                  )}
                  {(() => {
                    const tekst = (m.message || '').replace('\n[foto]', '').replace('[foto]', '').trim();
                    return tekst ? <div>{tekst}</div> : null;
                  })()}
                  <div style={{ fontSize: '10px', marginTop: '4px', opacity: 0.6, textAlign: 'right' }}>
                    {isAdmin ? 'Jij · ' : `${geselecteerdeKlant?.naam || 'Klant'} · `}{formatTijd(m.created_at)}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={bottomRef} />
        </div>

        {/* ── Invoer ── */}
        {selectedKlantId && (
          <div style={{ borderTop: '1px solid #e5e7eb', background: 'white' }}>
            <div style={{ padding: '6px 12px 0', display: 'flex', gap: '5px', flexWrap: 'wrap', alignItems: 'center' }}>
              {EMOJIS.map(emoji => (
                <button key={emoji} onClick={() => setNieuwBericht(prev => prev + emoji)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '17px', padding: '2px', lineHeight: 1 }}
                >{emoji}</button>
              ))}
              <label style={{ cursor: 'pointer', fontSize: '17px', lineHeight: 1, padding: '2px' }} title="Foto">
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFotoUpload} />
                📷
              </label>
            </div>
            {fotoPreview && (
              <div style={{ padding: '4px 12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={decodeImageData(fotoPreview)} alt="preview" style={{ height: '44px', borderRadius: '6px', border: '1px solid #e5e7eb' }} />
                <button onClick={() => { setFotoPreview(null); setFotoData(null); }}
                  style={{ background: '#ef4444', color: 'white', border: 'none', borderRadius: '50%', width: '20px', height: '20px', cursor: 'pointer', fontSize: '11px' }}>✕</button>
              </div>
            )}
            <div style={{ padding: '8px 12px 12px', display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
              <textarea
                ref={inputRef}
                value={nieuwBericht}
                onChange={e => setNieuwBericht(e.target.value)}
                onKeyDown={handleKey}
                placeholder={`Bericht naar ${geselecteerdeKlant?.naam || 'klant'}...`}
                rows={2}
                style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px', resize: 'none', outline: 'none', fontFamily: 'inherit' }}
              />
              <button onClick={verstuur}
                disabled={(!nieuwBericht.trim() && !fotoData) || sending}
                style={{
                  padding: '10px 14px', borderRadius: '8px', border: 'none', height: '42px',
                  cursor: (nieuwBericht.trim() || fotoData) && !sending ? 'pointer' : 'not-allowed',
                  background: (nieuwBericht.trim() || fotoData) && !sending ? '#1a364c' : '#e5e7eb',
                  color: (nieuwBericht.trim() || fotoData) && !sending ? 'white' : '#9ca3af',
                  display: 'flex', alignItems: 'center', gap: '4px', fontSize: '13px', fontWeight: '600'
                }}
              >
                <Send size={14} /> {sending ? '...' : 'Stuur'}
              </button>
            </div>
          </div>
        )}
      </div>
    </DraggableModal>
  );
};

export default BerichtenModal;
